import React, { useCallback, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import {
    Button,
    RadioGroup,
    SideBarToModal,
    Text,
} from 'reaxl';

import {
    kbbVRSDataDuck,
} from '@/ducks';

import {
    bapActiveInteractionDuck,
    bapConfigurationDuck,
    bapSelectedOptionsDuck,
} from '@/ducks/bap';

import bapOptionsMapping from './utilities/bapOptionsMapping';
import bapTitleHelper from './utilities/bapTitleHelper';

const BuildAndPriceWarningsModal = React.memo(
    () => {
        const activeConfiguration = useSelector(bapConfigurationDuck.selectors.getActiveConfiguration);
        const selectedOptions = useSelector(bapSelectedOptionsDuck.selectors.getDuckState);
        const trimInfo = useSelector(kbbVRSDataDuck.selectors.getSelectedTrimInfo);
        const { allOptions, trimId, vehicleId, vehicleIds } = trimInfo;

        const [numResolutionSelections, setNumResolutionSelections] = useState(0);
        const [resolutionSelections, setResolutionSelection] = useState({});
        const [showWarningModal, setShowWarningModal] = useState(false);

        const dispatch = useDispatch();

        const setActiveOptions = useCallback((selections) => dispatch(bapSelectedOptionsDuck.creators.setKeys(selections)), [dispatch]);
        const setActiveInteraction = useCallback((activeInteraction) => dispatch(bapActiveInteractionDuck.creators.setKeys(activeInteraction)), [dispatch]);

        // if there are warnings and the configuration service is not loading, then show the modal
        useEffect(() => {
            setShowWarningModal(activeConfiguration?.warnings?.length > 0 && !activeConfiguration?.loading);
        // check to show modal on each section preference change
        }, [activeConfiguration, selectedOptions]);

        // store selected option with the correlating vehicle option id
        // store resolution changes locally in order to update conflicts with the click of one button later
        const handleOptionChange = (event, currentSection) => {
            event.persist();
            const availableIds = vehicleIds?.[trimId];

            // if the current radio option is a drive type, then get the options for that vehicle id drive type, otherwise
            // get the vehicle options of the currently selected drive type
            const driveTrainName = currentSection === 'availableDriveTrainList' ? event?.target?.value
                : selectedOptions?.availableDriveTrainList?.optionName;
            const newVehicleId = availableIds?.[driveTrainName];
            const newVehicleIdOptions = availableIds?.[`${driveTrainName}-options`];

            const matchedOption = newVehicleIdOptions.filter((option) => option?.optionName === event?.target?.value)[0];

            for (const section in bapTitleHelper) {
                if (bapTitleHelper[section]?.optionsName === matchedOption?.categoryGroup) {
                    const currentResolutions = Object.assign(resolutionSelections, { [section]: matchedOption });
                    setResolutionSelection(currentResolutions);
                    setActiveInteraction({
                        lastTouched: section,
                        vehicleId: newVehicleId || vehicleId,
                    });
                    break;
                }
            }
            setNumResolutionSelections(Object.entries(resolutionSelections).length);
        };

        // Close modal and resolve conflicts
        // For each warning, determine if the fallback should be an existing option or if that option should be removed
        // Filter out conflicting packages and leave any remaining current selections
        // Reset local resolution selections
        const handleAutoDeselection = () => {
            setShowWarningModal(false);

            const unSelections = {};
            let currentPackages = selectedOptions.packageList;

            activeConfiguration?.warnings.forEach((warning) => {
                const childrenWarningOptions = allOptions.filter((option) => warning?.childIds.includes(option?.vehicleOptionId));
                const fallbackOptions = childrenWarningOptions.filter((option) => option.optionType !== 'Color');

                const defaultOption = fallbackOptions.length > 0 ? fallbackOptions[0] : null;

                // if warning has child ids (vehicle options ids) do a look up to see if the categoryGroup matches a
                // preference section, and assign the filtered out packages or default option to that section
                childrenWarningOptions.forEach((option) => {
                    const optionsKey = option?.optionType === 'Option' ? 'Packages' : option?.categoryGroup.replaceAll(' ', '');
                    const key = bapOptionsMapping[optionsKey].listName;

                    if (key === 'packageList') {
                        currentPackages = currentPackages.filter((currentPackage) => currentPackage.optionName !== option?.optionName);
                    } else {
                        Object.assign(unSelections, { [key]: defaultOption });
                    }
                });

            });
            Object.assign(unSelections, { packageList: currentPackages });

            // limit action options changes to one redux call
            setActiveOptions(unSelections);

            // reset helper variables
            setActiveInteraction({ lastTouched: null });
            setResolutionSelection({});
            setNumResolutionSelections(0);
        };

        // when user clicks update button, resolve conflicts all at once and reset local resolutions
        const handleResolveConflicts = () => {
        // If user has selected at least one possible resolution in the modal, then merge new selections with current
        // selections
        // If user did not select anything, automatically resolve & deselect conflicts
            const updatedSelections = {
                ...selectedOptions,
                ...resolutionSelections,
            };
            if (Object.entries(resolutionSelections).length > 0) {
                setActiveOptions(updatedSelections);
                setResolutionSelection({});
                setNumResolutionSelections(0);
            } else {
                handleAutoDeselection();
            }
        };

        // only render first warning so user can solve one issue at a time and does not create another conflict
        const renderWarnings = () => {
            const warning = activeConfiguration?.warnings?.[0];
            const childrenWarningOptions = !!allOptions && allOptions.filter((option) => warning?.childIds.includes(option?.vehicleOptionId));

            const hasValidWarnings = Object.keys(childrenWarningOptions).length > 0;

            const optionsKey = childrenWarningOptions?.[0]?.categoryGroup?.replace(/' '/, '');
            const sectionTitle = bapOptionsMapping?.[optionsKey]?.title;
            const currentSection = bapOptionsMapping?.[optionsKey]?.listName;

            const renderResolutionRadioGroup = () => {
                let warningButtons = null;

                if (hasValidWarnings) {
                    const radioOptions = childrenWarningOptions.map((childWarning) => ({
                        label: childWarning.optionName,
                        value: childWarning.optionName,
                    }));

                    warningButtons = (
                        <RadioGroup
                            className="margin-bottom-2 margin-top-4 margin-horizontal-2"
                            name="Warning Changes"
                            layout="vertical"
                            value="none"
                            options={radioOptions}
                            onChange={(event) => handleOptionChange(event, currentSection)}
                        />
                    );
                }

                return warningButtons;
            };

            return renderResolutionRadioGroup() !== null && (
                <div
                    className="margin-top-4"
                    key="warning"
                >
                    <Text weight="bold">
                        {sectionTitle}
                    </Text>
                    {renderResolutionRadioGroup()}
                </div>
            );
        };

        const renderContent = (
            <>
                This preference cannot be combined with your previous selections. Please choose from the list of available options.
                {renderWarnings()}
            </>
        );

        const renderFooter = () => (
            <div>
                <Button
                    block
                    bsStyle="primary"
                    disabled={numResolutionSelections < 1}
                    onClick={() => handleResolveConflicts()}
                >
                    Apply Changes
                </Button>
            </div>
        );

        return showWarningModal && (
            <SideBarToModal
                collapsed={false}
                content={renderContent}
                footerContent={renderFooter()}
                modalOptions={{
                    bsSize: 'small',
                    scrollPanel: true,
                }}
                onHide={() => handleResolveConflicts()}
                title={(
                    <Text
                        size={400}
                        weight="bold"
                    >
                        Configuration Not Available
                    </Text>
                )}
                show={showWarningModal}
                sideBarOptions={{
                    fullWidth: true,
                }}
            />
        );
    }
);

export default BuildAndPriceWarningsModal;
